/**
 * Fund and category colour tokens.
 *
 * The server stores a token like "emerald"; Tailwind cannot generate classes
 * from a runtime string, so the mapping has to be an explicit table. Keeping
 * it in one file means adding a colour is a one-line change rather than a hunt
 * through components.
 */

export interface ColorClasses {
  /** Solid fill — avatars, dots. */
  bg: string;
  /** Tinted surface — card headers, chips. */
  soft: string;
  /** Text on a tinted surface. */
  text: string;
  /** Border on a tinted surface. */
  border: string;
  /** Raw hex for Recharts, which cannot take a class. */
  hex: string;
}

const PALETTE: Record<string, ColorClasses> = {
  slate: { bg: 'bg-slate-500', soft: 'bg-slate-500/10', text: 'text-slate-500 dark:text-slate-400', border: 'border-slate-500/20', hex: '#64748b' },
  stone: { bg: 'bg-stone-500', soft: 'bg-stone-500/10', text: 'text-stone-500 dark:text-stone-400', border: 'border-stone-500/20', hex: '#78716c' },
  red: { bg: 'bg-red-500', soft: 'bg-red-500/10', text: 'text-red-600 dark:text-red-400', border: 'border-red-500/20', hex: '#ef4444' },
  orange: { bg: 'bg-orange-500', soft: 'bg-orange-500/10', text: 'text-orange-600 dark:text-orange-400', border: 'border-orange-500/20', hex: '#f97316' },
  amber: { bg: 'bg-amber-500', soft: 'bg-amber-500/10', text: 'text-amber-600 dark:text-amber-400', border: 'border-amber-500/20', hex: '#f59e0b' },
  lime: { bg: 'bg-lime-500', soft: 'bg-lime-500/10', text: 'text-lime-600 dark:text-lime-400', border: 'border-lime-500/20', hex: '#84cc16' },
  emerald: { bg: 'bg-emerald-500', soft: 'bg-emerald-500/10', text: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-500/20', hex: '#10b981' },
  teal: { bg: 'bg-teal-500', soft: 'bg-teal-500/10', text: 'text-teal-600 dark:text-teal-400', border: 'border-teal-500/20', hex: '#14b8a6' },
  cyan: { bg: 'bg-cyan-500', soft: 'bg-cyan-500/10', text: 'text-cyan-600 dark:text-cyan-400', border: 'border-cyan-500/20', hex: '#06b6d4' },
  sky: { bg: 'bg-sky-500', soft: 'bg-sky-500/10', text: 'text-sky-600 dark:text-sky-400', border: 'border-sky-500/20', hex: '#0ea5e9' },
  blue: { bg: 'bg-blue-500', soft: 'bg-blue-500/10', text: 'text-blue-600 dark:text-blue-400', border: 'border-blue-500/20', hex: '#3b82f6' },
  violet: { bg: 'bg-violet-500', soft: 'bg-violet-500/10', text: 'text-violet-600 dark:text-violet-400', border: 'border-violet-500/20', hex: '#8b5cf6' },
  pink: { bg: 'bg-pink-500', soft: 'bg-pink-500/10', text: 'text-pink-600 dark:text-pink-400', border: 'border-pink-500/20', hex: '#ec4899' },
  rose: { bg: 'bg-rose-500', soft: 'bg-rose-500/10', text: 'text-rose-600 dark:text-rose-400', border: 'border-rose-500/20', hex: '#f43f5e' },
};

export const COLOR_TOKENS = Object.keys(PALETTE);

export function colorClasses(token: string | null | undefined): ColorClasses {
  return PALETTE[token ?? 'slate'] ?? PALETTE['slate']!;
}

/**
 * Chart series colours, in an order that stays distinguishable — including for
 * the most common colour-vision deficiencies, which is why red and green are
 * not adjacent.
 */
export const CHART_COLORS = [
  '#0ea5e9', '#f59e0b', '#10b981', '#8b5cf6', '#f43f5e',
  '#06b6d4', '#84cc16', '#f97316', '#3b82f6', '#ec4899',
  '#14b8a6', '#a855f7',
];

export function chartColor(index: number): string {
  return CHART_COLORS[index % CHART_COLORS.length]!;
}
